import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import CategoryIcon from '@mui/icons-material/Category';
import FactoryIcon from '@mui/icons-material/Factory';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import StoreIcon from '@mui/icons-material/Store';
import { Box, Chip, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import useRoles from 'common/hooks/useRoles';
import { usePendingReversalsCount } from 'modules/ManageReversals';

const ALL_TABS = [
  {
    label: 'Inventory Management',
    path: '/inventory/dashboard',
    icon: <CategoryIcon fontSize='small' />,
    roleKey: null,
  },
  {
    label: 'Reversal Review',
    path: '/inventory/manage-reversals',
    icon: <AssignmentReturnIcon fontSize='small' />,
    roleKey: 'viewReversalReview',
  },
  {
    label: 'Stock Management',
    path: '/inventory/stockManagement',
    icon: <Inventory2Icon fontSize='small' />,
    roleKey: 'viewStockManagement',
  },
  {
    label: 'Vendor Management',
    path: '/inventory/vendors',
    icon: <StoreIcon fontSize='small' />,
    roleKey: 'viewVendorManagement',
  },
  {
    label: 'Manufacturer Management',
    path: '/inventory/manufacturers',
    icon: <FactoryIcon fontSize='small' />,
    roleKey: 'viewManufacturerManagement',
  },
  // POM-5974 — parked. The screen is reachable from Admin Center only for now;
  // the route and this entry come back together when it is wanted here too.
  // {
  //   label: 'Partner Preferences',
  //   path: '/inventory/partner-preferences',
  //   icon: <RuleIcon fontSize='small' />,
  //   roleKey: 'viewPartnerNdcPreference',
  // },
];

const InventoryManagement = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { count: pendingCount, refresh: refreshPendingCount } = usePendingReversalsCount();
  const roles = useRoles();

  const TABS = useMemo(
    () => ALL_TABS.filter((tab) => tab.roleKey === null || roles[tab.roleKey]),
    [roles]
  );

  useEffect(() => {
    document.title = 'Inventory - SmartOS';
  }, []);

  useEffect(() => {
    if (pathname === '/inventory') {
      navigate('/inventory/dashboard', { replace: true });
    }
  }, [pathname, navigate]);

  const activeTab = TABS.findIndex((t) => pathname.startsWith(t.path));

  const handleTabChange = (_, newIndex) => {
    navigate(TABS[newIndex].path);
  };

  return (
    <Box>
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography component='h1' variant='h5' sx={{ fontWeight: 600 }}>
          Inventory
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5, mb: 2 }}>
          Manage stock levels and inventory items across all locations.
        </Typography>
        <Tabs
          value={Math.max(0, activeTab)}
          onChange={handleTabChange}
          variant='scrollable'
          scrollButtons='auto'
          textColor='inherit'
          TabIndicatorProps={{ sx: { backgroundColor: 'primary.main', height: 3 } }}
          sx={{ borderBottom: 'none' }}
        >
          {TABS.map(({ label, icon, path }) => {
            const isReversals = path === '/inventory/manage-reversals';
            const tabLabel = isReversals ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {label}
                {pendingCount > 0 && (
                  <Chip
                    size='small'
                    label={pendingCount}
                    sx={{ bgcolor: '#c62828', color: '#fff', height: 18, fontSize: 11 }}
                  />
                )}
              </Box>
            ) : (
              label
            );
            return (
              <Tab
                key={label}
                icon={icon}
                iconPosition='start'
                label={tabLabel}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  gap: 0.5,
                  minHeight: 48,
                  '&.Mui-selected': { color: 'primary.main', fontWeight: 700 },
                }}
              />
            );
          })}
        </Tabs>
      </Box>
      {/* Lets Reversal Review re-read the badge after a resolution, since this shell stays
          mounted across tab changes and would otherwise hold a stale count. */}
      <Outlet context={{ refreshPendingCount }} />
    </Box>
  );
};

export default InventoryManagement;
